'use client';

function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'warning';
import * as pdfjs from 'pdfjs-dist';
import usePageContext from '../shared/hooks/usePageContext.js';
import useResolver from '../shared/hooks/useResolver.js';
import { cancelRunningTask } from '../shared/utils.js';
function isTextItem(item) {
  return 'str' in item;
}
export default function TextLayer() {
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var customTextRenderer = pageContext.customTextRenderer,
    onGetTextError = pageContext.onGetTextError,
    onGetTextSuccess = pageContext.onGetTextSuccess,
    onRenderTextLayerError = pageContext.onRenderTextLayerError,
    onRenderTextLayerSuccess = pageContext.onRenderTextLayerSuccess,
    page = pageContext.page,
    pageIndex = pageContext.pageIndex,
    pageNumber = pageContext.pageNumber,
    rotate = pageContext.rotate,
    scale = pageContext.scale;
  invariant(page, 'Attempted to load page text content, but no page was specified.');
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    textContentState = _useResolver2[0],
    textContentDispatch = _useResolver2[1];
  var textContent = textContentState.value,
    textContentError = textContentState.error;
  var layerElement = useRef(null);
  warning(Number.parseInt(window.getComputedStyle(document.body).getPropertyValue('--react-pdf-text-layer'), 10) === 1, 'TextLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-text-layer');
  /**
   * Called when a page text content is read successfully
   */
  function onLoadSuccess() {
    if (!textContent) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onGetTextSuccess) {
      onGetTextSuccess(textContent);
    }
  }
  /**
   * Called when a page text content failed to read successfully
   */
  function onLoadError() {
    if (!textContentError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, textContentError.toString());
    if (onGetTextError) {
      onGetTextError(textContentError);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on page change
  useEffect(function resetTextContent() {
    textContentDispatch({
      type: 'RESET'
    });
  }, [page, textContentDispatch]);
  useEffect(function loadTextContent() {
    if (!page) {
      return;
    }
    var cancellable = makeCancellable(page.getTextContent());
    var runningTask = cancellable;
    cancellable.promise.then(function (nextTextContent) {
      textContentDispatch({
        type: 'RESOLVE',
        value: nextTextContent
      });
    }).catch(function (error) {
      textContentDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      return cancelRunningTask(runningTask);
    };
  }, [page, textContentDispatch]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
  useEffect(function () {
    if (textContent === undefined) {
      return;
    }
    if (textContent === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  }, [textContent]);
  /**
   * Called when a text layer is rendered successfully
   */
  var onRenderSuccess = useCallback(function () {
    if (onRenderTextLayerSuccess) {
      onRenderTextLayerSuccess();
    }
  }, [onRenderTextLayerSuccess]);
  /**
   * Called when a text layer failed to render successfully
   */
  var onRenderError = useCallback(function (error) {
    warning(false, error.toString());
    if (onRenderTextLayerError) {
      onRenderTextLayerError(error);
    }
  }, [onRenderTextLayerError]);
  function onMouseDown() {
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    layer.classList.add('selecting');
  }
  function onMouseUp() {
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    layer.classList.remove('selecting');
  }
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  useLayoutEffect(function renderTextLayer() {
    if (!page || !textContent) {
      return;
    }
    var layer = layerElement.current;
    if (!layer) {
      return;
    }
    layer.innerHTML = '';
    var textContentSource = page.streamTextContent({
      includeMarkedContent: true
    });
    var parameters = {
      container: layer,
      textContentSource: textContentSource,
      viewport: viewport
    };
    var cancellable = new pdfjs.TextLayer(parameters);
    var runningTask = cancellable;
    cancellable.render().then(function () {
      var end = document.createElement('div');
      end.className = 'endOfContent';
      layer.append(end);
      var layerChildren = layer.querySelectorAll('[role="presentation"]');
      if (customTextRenderer) {
        var index = 0;
        textContent.items.forEach(function (item, itemIndex) {
          if (!isTextItem(item)) {
            return;
          }
          var child = layerChildren[index];
          if (!child) {
            return;
          }
          var content = customTextRenderer(Object.assign({
            pageIndex: pageIndex,
            pageNumber: pageNumber,
            itemIndex: itemIndex
          }, item));
          child.innerHTML = content;
          index += item.str && item.hasEOL ? 2 : 1;
        });
      }
      // Intentional immediate callback
      onRenderSuccess();
    }).catch(onRenderError);
    return function () {
      return cancelRunningTask(runningTask);
    };
  }, [customTextRenderer, onRenderError, onRenderSuccess, page, pageIndex, pageNumber, textContent, viewport]);
  return _jsx("div", {
    className: clsx('react-pdf__Page__textContent', 'textLayer'),
    onMouseUp: onMouseUp,
    onMouseDown: onMouseDown,
    ref: layerElement
  });
}